/**
 * variant-id-fix.js
 * ------------------------------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * On combined-listing products, Shopify's Section Rendering resolution of
 * `products/<handle>?option_values=<id,id,...>` cannot resolve the LAST (product-differentiating)
 * option value: it always returns the FIRST value's variant, no matter which option-value id is
 * sent — even when the requested combination exists and is available.
 *
 * The bundled variant-picker (`theme.js`) switches variants exclusively through that
 * `?option_values=` endpoint, so the affected option (e.g. "Ottomane"/"Ausrichtung": LINKS/RECHTS,
 * or "Sofagarnitur") can never be switched — clicking it flickers and snaps back to the first value.
 *
 * Addressing the very same variant by id — `products/<handle>?variant=<id>` — resolves correctly.
 *
 * WHAT THIS DOES
 *
 * Wraps `window.fetch` and, for same-product variant-picker requests that carry `option_values`,
 * rewrites the URL to the equivalent `?variant=<id>` (mapping option-value ids -> value names via
 * the rendered inputs, then names -> variant id via the product's variants JSON). All other
 * requests — including combined-listing navigations to a *different* product handle — pass through
 * untouched, so the existing behaviour is preserved everywhere it already works.
 *
 * `theme.js` is a prebuilt esbuild bundle with no source in the repo, so this correction lives as a
 * standalone asset instead of a bundle edit (same approach as equalize-options.js).
 *
 * NOTE: this only fixes variant *switching*. The initial/rerendered "which radio is checked" state
 * is corrected in Liquid (snippets/option-value.liquid via snippets/variant-picker.liquid), because
 * Shopify also reports `option_value.selected == true` for both values of the affected option.
 */
(function () {
  if (window.__variantIdFixInstalled) return;
  window.__variantIdFixInstalled = true;

  var SEP = '␟'; // unit separator — safe join key for option-value name tuples
  var origFetch = window.fetch;
  if (typeof origFetch !== 'function') return;

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var variantMapCache = {}; // handle -> Promise<{ "name1␟name2␟…": variantId }>

  // Build an option-value-id -> value-name map from every rendered option input currently in the DOM.
  // Option-value ids are stable across variant changes, so reading the live DOM at request time is safe.
  function buildIdNameMap() {
    var map = {};
    var inputs = document.querySelectorAll('input[data-option-position][data-option-name][value]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      map[el.value] = el.getAttribute('data-option-name');
    }
    return map;
  }

  // Handles that are actually rendered as pickers on this page (main product + any quick-buy products).
  // A combined-listing link points at a handle NOT rendered here, so it is intentionally excluded.
  function pickerHandles() {
    var set = {};
    var pickers = document.querySelectorAll('variant-picker[handle]');
    for (var i = 0; i < pickers.length; i++) {
      set[pickers[i].getAttribute('handle')] = true;
    }
    return set;
  }

  function getVariantMap(handle) {
    if (!variantMapCache[handle]) {
      variantMapCache[handle] = origFetch(root + 'products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          var m = {};
          (p.variants || []).forEach(function (v) {
            m[(v.options || []).join(SEP)] = v.id;
          });
          return m;
        })
        .catch(function () { return {}; });
    }
    return variantMapCache[handle];
  }

  function rewrite(url, thisArg, args) {
    var u;
    try {
      u = new URL(url, window.location.origin);
    } catch (e) {
      return origFetch.apply(thisArg, args);
    }

    var ov = u.searchParams.get('option_values');
    var pathMatch = u.pathname.match(/\/products\/([^/?]+)/);
    var handle = pathMatch && pathMatch[1];

    if (!ov || !handle || !pickerHandles()[handle]) {
      return origFetch.apply(thisArg, args); // not a same-product picker request — leave untouched
    }

    var idNameMap = buildIdNameMap();
    var ids = ov.split(',');
    var names = [];
    for (var i = 0; i < ids.length; i++) {
      var name = idNameMap[ids[i]];
      if (name == null) return origFetch.apply(thisArg, args); // can't map every id — bail out safely
      names.push(name);
    }

    return getVariantMap(handle).then(function (vmap) {
      var vid = vmap[names.join(SEP)];
      if (!vid) return origFetch.apply(thisArg, args); // no matching variant — leave untouched
      u.searchParams.delete('option_values');
      u.searchParams.set('variant', String(vid));
      return origFetch(u.toString(), args[1]);
    }).catch(function () {
      return origFetch.apply(thisArg, args);
    });
  }

  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (url && url.indexOf('option_values=') !== -1 && url.indexOf('/products/') !== -1) {
        return rewrite(url, this, arguments);
      }
    } catch (e) { /* fall through to original */ }
    return origFetch.apply(this, arguments);
  };
})();
